// Where a visitor came from, for the website forms (Get started, sign-up, booking).
//
// wallmates.com's embed scripts remember the first and latest tagged visit in a `wm_attr` cookie on
// .wallmates.com and pass it to our frames as ?wm_attr=. This file reads that (or the cookie itself on
// a top-level page), records visits that land on the portal directly (an ad pointing at /new-account),
// and turns it all into the `attribution` block the CRM intake accepts. The CRM cleans the values;
// here they are only cut to length.

const TAGS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'] as const;
const CLICKS = ['gclid', 'gbraid', 'wbraid', 'msclkid', 'ttclid', 'fbclid'] as const;
const COOKIE = 'wm_attr';
const MAX_AGE = 90 * 86_400;

/** A remembered visit, in the CRM intake's touch shape. */
export interface StoredTouch {
    source?: string; medium?: string; campaign?: string; content?: string; term?: string; utm_id?: string;
    gclid?: string; gbraid?: string; wbraid?: string; msclkid?: string; ttclid?: string; fbclid?: string;
    landing_path?: string; referrer?: string; at?: string;
}
export interface StoredAttribution { f?: StoredTouch; l?: StoredTouch; fbc?: string; fbp?: string }

/** The intake's attribution block. The flat fields describe the page the form was opened from. */
export interface FormAttribution {
    utm_source: string; utm_medium: string; utm_campaign: string; utm_content: string; utm_term: string;
    landing_path: string; referrer_origin: string; entry_point: string;
    utm_id?: string; gclid?: string; gbraid?: string; wbraid?: string; msclkid?: string; ttclid?: string; fbclid?: string;
    fbc?: string; fbp?: string; first_touch?: StoredTouch; last_touch?: StoredTouch;
}

// #region readCookie
const readCookie = (name: string): string | null => {
    try {
        const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
        return m ? decodeURIComponent(m[1]) : null;
    } catch {
        return null;
    }
};

/** Our own sites (and this one): coming from them is moving around, not arriving. */
const ownSite = (host: string): boolean => host === window.location.hostname || /(^|\.)(wallmates|wallblush)\.com$/i.test(host);

// #region cleanTouch
/** Only known keys, as short strings: whatever arrives on the address is someone else's text. */
const cleanTouch = (t: unknown): StoredTouch | undefined => {
    if (!t || typeof t !== 'object') return undefined;
    const out: Record<string, string> = {};
    const keys = ['source', 'medium', 'campaign', 'content', 'term', 'utm_id', ...CLICKS, 'landing_path', 'referrer', 'at'];
    for (const k of keys) {
        const v = (t as Record<string, unknown>)[k];
        if (typeof v === 'string' && v.trim()) out[k] = v.trim().slice(0, 500);
    }
    return Object.keys(out).length ? out as StoredTouch : undefined;
};

// #region readStored
/** The remembered touches: from ?wm_attr= when a wallmates.com page passed them, else this site's cookie. */
export const readStored = (params: URLSearchParams): StoredAttribution => {
    let parsed: unknown = null;
    for (const text of [params.get('wm_attr'), readCookie(COOKIE)]) {
        if (!text || text.length > 6000) continue;
        try { parsed = JSON.parse(text); break; } catch { /* try the next */ }
    }
    const p = (parsed && typeof parsed === 'object' ? parsed : {}) as Record<string, unknown>;
    const str = (v: unknown, max: number) => (typeof v === 'string' && v ? v.slice(0, max) : undefined);
    return {
        f: cleanTouch(p.f), l: cleanTouch(p.l),
        fbc: str(p.fbc, 500) ?? str(readCookie('_fbc'), 500), fbp: str(p.fbp, 200) ?? str(readCookie('_fbp'), 200)
    };
};

// #region visitTouch
/** This address as a touch, or null when it says nothing (no tags, no ad click, no other site). */
export const visitTouch = (href: string, referrer: string, now = new Date()): StoredTouch | null => {
    const here = new URL(href);
    const t: Record<string, string> = {};
    for (const k of TAGS) { const v = here.searchParams.get(k); if (v) t[k === 'utm_id' ? 'utm_id' : k.replace('utm_', '')] = v.slice(0, 250); }
    for (const k of CLICKS) { const v = here.searchParams.get(k); if (v) t[k] = v.slice(0, 500); }
    try { const r = new URL(referrer); if (!ownSite(r.hostname)) t.referrer = r.origin; } catch { /* none */ }
    if (!Object.keys(t).length) return null;
    return { ...t, landing_path: here.pathname.slice(0, 500), at: now.toISOString() };
};

// #region rememberLanding
/**
 * A visit that lands on the portal itself with tags (an ad linking straight to sign-up) is remembered
 * the same way the website does it. Skipped inside our own frames: the host page already did it.
 */
export const rememberLanding = (): void => {
    try {
        if (window.parent !== window) return;
        const t = visitTouch(window.location.href, document.referrer);
        if (!t) return;
        const stored = readStored(new URLSearchParams());
        const value = encodeURIComponent(JSON.stringify({ f: stored.f ?? t, l: t }));
        const domain = /(^|\.)wallmates\.com$/i.test(window.location.hostname) ? '; domain=.wallmates.com' : '';
        const secure = window.location.protocol === 'https:' ? '; secure' : '';
        document.cookie = `${COOKIE}=${value}; path=/; max-age=${MAX_AGE}; samesite=lax${secure}${domain}`;
    } catch {
        /* cookies off: forms still send the page's own tags */
    }
};

// #region formAttribution
/**
 * The attribution block for a form opened at this address: the host page's tags and click ids
 * (passed on the address by the embed), the referrer, Meta's cookies and the remembered touches.
 */
export const formAttribution = (params: URLSearchParams, entryDefault: string, stored: StoredAttribution = readStored(params)): FormAttribution => {
    const cut = (k: string, max: number) => (params.get(k) ?? '').slice(0, max);
    let referrer = '';
    try { const r = new URL(params.get('ref') ?? ''); if (!ownSite(r.hostname)) referrer = r.origin.slice(0, 500); } catch { /* none */ }
    const out: FormAttribution = {
        utm_source: cut('utm_source', 250), utm_medium: cut('utm_medium', 250), utm_campaign: cut('utm_campaign', 250),
        utm_content: cut('utm_content', 250), utm_term: cut('utm_term', 250),
        landing_path: cut('landing_path', 500), referrer_origin: referrer, entry_point: (params.get('src') || entryDefault).slice(0, 100)
    };
    const utmId = cut('utm_id', 150);
    if (utmId) out.utm_id = utmId;
    for (const k of CLICKS) { const v = cut(k, 500); if (v) out[k] = v; }
    if (stored.fbc) out.fbc = stored.fbc;
    if (stored.fbp) out.fbp = stored.fbp;
    if (stored.f) out.first_touch = stored.f;
    if (stored.l) out.last_touch = stored.l;
    return out;
};
